import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Copy, Send, Check, X, Layers, LineChart, Loader2,
  Building2, Clock, Trash2, ExternalLink, RotateCw, ChevronDown, Download, Mail,
  AlertCircle,
} from 'lucide-react';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const STATUSES = [
  { id: 'draft', label: 'Draft' },
  { id: 'sent', label: 'Sent' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'expired', label: 'Expired' },
];

const QuoteDetailPage = () => {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusOpen, setStatusOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/quotes/${id}`);
      setQuote(r.data);
      setSelectedVersion(r.data.version);
    } catch (e) { navigate('/admin/quotes'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (status) => {
    try {
      await api.patch(`/quotes/${id}/status`, { status });
      setStatusOpen(false);
      load();
    } catch (e) { /* */ }
  };

  const remove = async () => {
    if (!window.confirm(`Delete quote ${quote.quote_number}? This removes all versions.`)) return;
    try { await api.delete(`/quotes/${id}`); navigate('/admin/quotes'); } catch (e) { /* */ }
  };

  const shareUrl = quote ? `${window.location.origin}/quote/${quote.id}` : '';
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: quote?.currency || 'USD', maximumFractionDigits: 0 }).format(n || 0);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/quotes/${id}/pdf`, { responseType: 'blob', params: { version: v?.version_number } });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quote.quote_number}_v${v.version_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert('Failed to download PDF'); }
  };

  const [emailOpen, setEmailOpen] = useState(false);

  if (loading || !quote) {
    return <div className="p-10 text-white/50"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const v = selectedVersion;
  const isGst = (v.tax_label || '').toUpperCase().includes('GST');

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px]">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/quotes" className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors">
          <ArrowLeft className="w-4 h-4 text-white/70" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2 py-1 rounded-full border ${
              quote.product === 'pti' ? 'text-cyan-300 border-cyan-400/30 bg-cyan-400/10' : 'text-purple-300 border-purple-400/30 bg-purple-400/10'
            }`}>
              {quote.product === 'pti' ? <LineChart className="w-3 h-3" /> : <Layers className="w-3 h-3" />}
              {quote.product.toUpperCase()}
            </span>
            <h1 className="text-2xl font-semibold text-white tracking-tight font-mono">{quote.quote_number}</h1>
            <span className="text-white/50 text-sm">· v{v.version_number}</span>
          </div>
          <div className="mt-1 text-[13px] text-white/55">{v.customer_snapshot?.name || '—'}</div>
        </div>

        {/* Status pill / picker */}
        <div className="relative">
          <button onClick={() => setStatusOpen((o) => !o)} className="h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white font-semibold text-[12.5px] flex items-center gap-2 capitalize">
            {quote.status}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${statusOpen ? 'rotate-180' : ''}`} />
          </button>
          {statusOpen && (
            <div className="absolute right-0 mt-2 w-44 rounded-lg bg-[#12121a] border border-white/10 shadow-2xl overflow-hidden z-20">
              {STATUSES.map((s) => (
                <button key={s.id} onClick={() => setStatus(s.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-[13px] transition-colors ${
                    quote.status === s.id ? 'bg-white/5 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}>
                  {s.label}
                  {quote.status === s.id && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <button onClick={() => navigator.clipboard.writeText(shareUrl)} className="h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/85 font-semibold text-[12.5px] flex items-center gap-2">
          <Copy className="w-3.5 h-3.5" /> Copy link
        </button>
        <button onClick={downloadPdf} className="h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/85 font-semibold text-[12.5px] flex items-center gap-2">
          <Download className="w-3.5 h-3.5" /> PDF
        </button>
        <button onClick={() => setEmailOpen(true)} className="h-9 px-3 rounded-lg bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-[12.5px] flex items-center gap-2">
          <Mail className="w-3.5 h-3.5" /> Email
        </button>
        <a href={`/quote/${quote.id}`} target="_blank" rel="noreferrer" className="h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/85 font-semibold text-[12.5px] flex items-center gap-2">
          <ExternalLink className="w-3.5 h-3.5" /> Preview
        </a>
        {isAdmin && (
          <button onClick={remove} className="h-9 w-9 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-400/10 border border-white/10 inline-flex items-center justify-center">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Left: quote content */}
        <div className="space-y-6">
          {/* Customer + Meta */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50">Quotation for</div>
                <div className="mt-2 text-lg font-semibold text-white">{v.customer_snapshot?.name}</div>
                {v.customer_snapshot?.contact_name && <div className="text-white/70 text-[13.5px]">{v.customer_snapshot.contact_name}</div>}
                {v.customer_snapshot?.contact_email && <div className="text-white/50 text-[13px]">{v.customer_snapshot.contact_email}</div>}
                {v.customer_snapshot?.billing_address && <div className="mt-2 text-[13px] text-white/60 whitespace-pre-line">{v.customer_snapshot.billing_address}</div>}
                {v.customer_snapshot?.tax_id && <div className="mt-2 text-[12.5px] text-white/60">Tax ID: <span className="font-mono text-white/80">{v.customer_snapshot.tax_id}</span></div>}
              </div>
              <div className="grid grid-cols-2 gap-4 text-[13px]">
                <Meta label="Quote number" value={quote.quote_number} mono />
                <Meta label="Version" value={`v${v.version_number}`} />
                <Meta label="Currency" value={quote.currency} mono />
                <Meta label="Valid until" value={v.valid_until || '—'} />
                <Meta label="Created" value={new Date(v.created_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' })} />
                <Meta label="Status" value={<span className="capitalize text-white">{quote.status}</span>} />
              </div>
            </div>
            {v.custom_fields?.length > 0 && (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                {v.custom_fields.map((f) => (
                  <Meta key={f.id} label={f.label} value={f.value || '—'} />
                ))}
              </div>
            )}
          </div>

          {/* Line items table */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="text-white font-semibold">Line items</div>
              <div className="text-[11px] text-white/45">{v.line_items.length} item{v.line_items.length !== 1 ? 's' : ''}</div>
            </div>
            <div className="overflow-x-auto"><table className="w-full text-[13.5px] min-w-[640px]">
              <thead>
                <tr className="border-b border-white/10 text-left text-[10px] tracking-[0.18em] font-semibold uppercase text-white/50">
                  <th className="px-6 py-3">Description</th>
                  <th className="px-3 py-3">HSN/SAC</th>
                  <th className="px-3 py-3">Recurring</th>
                  <th className="px-3 py-3 text-right">Qty</th>
                  <th className="px-3 py-3 text-right">Unit price</th>
                  <th className="px-6 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {v.line_items.map((li) => (
                  <tr key={li.id} className="border-b border-white/5 last:border-0">
                    <td className="px-6 py-3 text-white/85">{li.description}</td>
                    <td className="px-3 py-3 font-mono text-[12px] text-white/55">{li.hsn_sac || '—'}</td>
                    <td className="px-3 py-3 text-white/60 capitalize">{(li.recurring || '').replace('_', '-')}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-white/80">{li.qty}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-white/80">{fmt(li.unit_price)}</td>
                    <td className="px-6 py-3 text-right tabular-nums text-white font-semibold">{fmt(li.qty * li.unit_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>

          {/* Notes */}
          {v.notes && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50 mb-2">Internal notes</div>
              <div className="text-[13.5px] text-white/70 whitespace-pre-line">{v.notes}</div>
            </div>
          )}

          {/* T&Cs */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <div className="text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50 mb-3">Terms & conditions</div>
            <pre className="whitespace-pre-wrap font-sans text-[13px] text-white/70 leading-relaxed">{v.terms_and_conditions}</pre>
          </div>
        </div>

        {/* Right: totals + version history */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-6">
            <div className="text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50 mb-3">Totals</div>
            <div className="space-y-2 text-[13.5px]">
              <Row label="Subtotal" value={fmt(v.subtotal)} />
              {v.discount_total > 0 && <Row label={`Discount${v.discount_pct > 0 ? ` (${(v.discount_pct * 100).toFixed(0)}%)` : ''}`} value={`− ${fmt(v.discount_total)}`} red />}
              <div className="pt-2 border-t border-white/5">
                <Row label="Taxable" value={fmt(v.taxable_amount)} />
              </div>
              {v.tax_rate > 0 && isGst && v.is_intra_state ? (
                <>
                  <Row sub label={`CGST (${(v.tax_rate * 50).toFixed(1)}%)`} value={fmt(v.tax_total / 2)} />
                  <Row sub label={`SGST (${(v.tax_rate * 50).toFixed(1)}%)`} value={fmt(v.tax_total / 2)} />
                </>
              ) : v.tax_rate > 0 && isGst ? (
                <Row sub label={`IGST (${(v.tax_rate * 100).toFixed(1)}%)`} value={fmt(v.tax_total)} />
              ) : v.tax_rate > 0 ? (
                <Row sub label={`${v.tax_label} (${(v.tax_rate * 100).toFixed(1)}%)`} value={fmt(v.tax_total)} />
              ) : null}
            </div>
            <div className="mt-4 rounded-xl border border-emerald-400/25 bg-emerald-400/[0.05] p-4 flex items-center justify-between">
              <span className="text-[11px] tracking-[0.18em] uppercase font-semibold text-emerald-300">Grand total</span>
              <span className="text-xl font-bold text-white tabular-nums">{fmt(v.grand_total)}</span>
            </div>
          </div>

          {/* Version history */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <div className="text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50 mb-3 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" /> Version history
            </div>
            <div className="space-y-1.5">
              {quote.all_versions.map((ver) => (
                <button key={ver.id} onClick={() => setSelectedVersion(ver)}
                  className={`w-full text-left flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    selectedVersion?.id === ver.id ? 'bg-emerald-400/10 border border-emerald-400/30' : 'bg-white/[0.02] hover:bg-white/[0.05] border border-white/5'
                  }`}>
                  <div>
                    <div className="text-[13px] text-white font-semibold">v{ver.version_number}
                      {ver.id === quote.current_version_id && <span className="ml-2 text-[10px] tracking-[0.16em] uppercase text-emerald-300">Current</span>}
                    </div>
                    <div className="text-[11.5px] text-white/45">{new Date(ver.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                  <div className="text-[13px] text-white tabular-nums font-semibold">{fmt(ver.grand_total)}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {emailOpen && <EmailQuoteDialog quote={quote} version={v} onClose={() => setEmailOpen(false)} onSent={load} />}
    </div>
  );
};

const EmailQuoteDialog = ({ quote, version, onClose, onSent }) => {
  const [to, setTo] = useState(version.customer_snapshot?.contact_email || '');
  const [subject, setSubject] = useState(`Quotation ${quote.quote_number} · Itanta`);
  const [message, setMessage] = useState('');
  const [ccSales, setCcSales] = useState(true);
  const [includePdf, setIncludePdf] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const send = async () => {
    setSending(true); setErr('');
    try {
      const emails = to.split(',').map((e) => e.trim()).filter(Boolean);
      const r = await api.post(`/quotes/${quote.id}/email`, {
        to: emails,
        subject,
        message,
        include_pdf: includePdf,
        cc_sales: ccSales,
      });
      setResult(r.data);
      onSent && onSent();
    } catch (ex) {
      setErr(errorMessage(ex, 'Failed to send email'));
    }
    setSending(false);
  };

  const emailReport = result?.email;
  const mocked = emailReport?.mocked;
  const sent = result && !mocked && !emailReport?.error;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#12121a] p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="text-white font-semibold text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-emerald-300" />
            Email quotation
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {result ? (
          <div className="space-y-4">
            {sent && (
              <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.06] p-4">
                <div className="flex items-center gap-2 text-emerald-300 font-semibold">
                  <Check className="w-4 h-4" /> Email sent
                </div>
                <div className="mt-2 text-[13px] text-white/70">Recipients: {result.sent_to.join(', ')}</div>
              </div>
            )}
            {mocked && (
              <div className="rounded-xl border border-yellow-400/30 bg-yellow-400/[0.06] p-4">
                <div className="flex items-center gap-2 text-yellow-300 font-semibold">
                  <AlertCircle className="w-4 h-4" /> Sent in MOCKED mode
                </div>
                <div className="mt-2 text-[12.5px] text-white/70">
                  Email service is not configured. The email was logged but not delivered. Add EMERGENT_EMAIL_KEY to backend/.env to enable delivery.
                </div>
              </div>
            )}
            {emailReport?.error && (
              <div className="rounded-xl border border-red-400/30 bg-red-400/[0.06] p-4 text-[13px] text-red-200">
                <div className="font-semibold">Email error {emailReport.status_code || ''}</div>
                <div className="mt-1 text-white/70 font-mono text-[11.5px] whitespace-pre-wrap">{emailReport.error}</div>
              </div>
            )}
            <button onClick={onClose} className="w-full h-10 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/85 font-semibold text-sm">Close</button>
          </div>
        ) : (
          <div className="space-y-4">
            <label className="block">
              <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/55">To (comma separated)</span>
              <input required value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com"
                className="mt-1.5 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]" />
            </label>
            <label className="block">
              <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/55">Subject</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                className="mt-1.5 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]" />
            </label>
            <label className="block">
              <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/55">Message</span>
              <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Optional note to the customer…"
                className="mt-1.5 w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px] resize-y" />
            </label>
            <div className="flex items-center gap-5">
              <label className="flex items-center gap-2 text-[13px] text-white/75">
                <input type="checkbox" checked={includePdf} onChange={(e) => setIncludePdf(e.target.checked)} className="h-4 w-4" />
                Attach PDF
              </label>
              <label className="flex items-center gap-2 text-[13px] text-white/75">
                <input type="checkbox" checked={ccSales} onChange={(e) => setCcSales(e.target.checked)} className="h-4 w-4" />
                Notify internal sales
              </label>
            </div>
            {err && <div className="text-[13px] text-red-300">{err}</div>}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button onClick={onClose} className="h-10 px-5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 font-semibold text-sm">Cancel</button>
              <button onClick={send} disabled={sending || !to} className="h-10 px-5 rounded-lg bg-emerald-400 hover:bg-emerald-300 disabled:opacity-60 text-black font-semibold text-sm inline-flex items-center gap-2">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending…' : 'Send email'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Meta = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] tracking-[0.18em] font-semibold uppercase text-white/45">{label}</div>
    <div className={`mt-1 text-white/90 ${mono ? 'font-mono text-[13px]' : 'text-[13.5px]'}`}>{value}</div>
  </div>
);
const Row = ({ label, value, sub, red }) => (
  <div className={`flex items-center justify-between ${sub ? 'text-[12.5px] text-white/60 pl-2' : 'text-white/80'}`}>
    <span>{label}</span>
    <span className={`tabular-nums ${red ? 'text-red-300' : 'text-white'}`}>{value}</span>
  </div>
);

export default QuoteDetailPage;
